import React from 'react';
import { 
  Terminal, 
  RotateCw, 
  Sliders, 
  Wifi, 
  WifiOff, 
  Activity, 
  Pause, 
  Play,
  ShieldCheck
} from 'lucide-react';
import { GatewayConfig, PollInterval } from '../types';

interface HeaderProps {
  config: GatewayConfig;
  pollInterval: PollInterval;
  onChangeInterval: (interval: PollInterval) => void;
  secondsRemaining: number;
  isPolling: boolean;
  onRefreshNow: () => void;
  onOpenConfig: () => void;
  isSimulated: boolean;
  statusMessage: string;
  activeTab: 'overview' | 'security';
  onChangeTab: (tab: 'overview' | 'security') => void;
  securityIncidentCount: number;
}

export const Header: React.FC<HeaderProps> = ({
  config,
  pollInterval,
  onChangeInterval,
  secondsRemaining,
  isPolling,
  onRefreshNow,
  onOpenConfig,
  isSimulated,
  statusMessage,
  activeTab,
  onChangeTab,
  securityIncidentCount
}) => {
  const isPaused = pollInterval === 0;

  // Calculate percentage of countdown for visual indicator
  const totalDuration = isPaused ? 1 : pollInterval;
  const progressPct = isPaused ? 0 : Math.max(0, Math.min(100, (secondsRemaining / totalDuration) * 100));

  return (
    <header className="border-b border-slate-800 bg-[#0b0f19]/90 backdrop-blur-md sticky top-0 z-30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row items-center justify-between py-3.5 gap-4">
          
          {/* Logo & System Brand */}
          <div className="flex items-center justify-between w-full lg:w-auto">
            <div className="flex items-center gap-3">
              <div className="relative flex items-center justify-center w-9 h-9 rounded-lg bg-gradient-to-br from-cyan-500/20 to-blue-600/30 border border-cyan-500/40 text-cyan-400 glow-cyan">
                <Terminal className="w-5 h-5" />
                <span className="absolute -top-1 -right-1 flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-cyan-500"></span>
                </span>
              </div>
              
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-base font-bold tracking-tight text-white flex items-center gap-1.5 font-sans">
                    COMPUTER CONTROLLER
                    <span className="text-[10px] uppercase font-mono px-1.5 py-0.5 rounded bg-slate-800 text-slate-300 border border-slate-700">
                      OBSERVABILITY
                    </span>
                  </h1>
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-400 font-mono">
                  <span className="flex items-center gap-1 text-slate-300">
                    <span className="text-cyan-400">@</span>
                    {config.baseUrl ? new URL(config.baseUrl).hostname : '9router.hanavy.online'}
                  </span>
                  <span className="text-slate-600">•</span>
                  <span className={isSimulated ? 'text-amber-400/90' : 'text-emerald-400/90'}>
                    {isSimulated ? 'SYNTHETIC_STREAM' : 'LIVE_GATEWAY'}
                  </span>
                </div>
              </div>
            </div>

            {/* Navigation Tabs (Mobile Compact) */}
            <div className="flex lg:hidden items-center bg-slate-900/80 p-1 rounded-lg border border-slate-800">
              <button
                onClick={() => onChangeTab('overview')}
                className={`px-2.5 py-1 text-xs rounded font-medium transition-colors ${
                  activeTab === 'overview'
                    ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30'
                    : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                Feed
              </button>
              <button
                onClick={() => onChangeTab('security')}
                className={`flex items-center gap-1 px-2.5 py-1 text-xs rounded font-medium transition-colors ${
                  activeTab === 'security'
                    ? 'bg-rose-500/20 text-rose-300 border border-rose-500/30'
                    : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                Security
                {securityIncidentCount > 0 && (
                  <span className="px-1 rounded bg-rose-500/30 text-rose-300 text-[10px] font-mono">
                    {securityIncidentCount}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Center Tabs (Desktop) */}
          <div className="hidden lg:flex items-center bg-slate-900/90 p-1 rounded-lg border border-slate-800 shadow-inner">
            <button
              onClick={() => onChangeTab('overview')}
              className={`flex items-center gap-2 px-3.5 py-1.5 text-xs rounded-md font-medium transition-all ${
                activeTab === 'overview'
                  ? 'bg-gradient-to-r from-slate-800 to-slate-850 text-cyan-300 border border-slate-700 shadow-sm'
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/40'
              }`}
            >
              <Activity className="w-3.5 h-3.5 text-cyan-400" />
              <span>Telemetry Matrix</span>
            </button>
            <button
              onClick={() => onChangeTab('security')}
              className={`flex items-center gap-2 px-3.5 py-1.5 text-xs rounded-md font-medium transition-all ${
                activeTab === 'security'
                  ? 'bg-gradient-to-r from-rose-950/40 to-slate-850 text-rose-300 border border-rose-900/60 shadow-sm'
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/40'
              }`}
            >
              <ShieldCheck className="w-3.5 h-3.5 text-rose-400" />
              <span>Security Guardrails</span>
              {securityIncidentCount > 0 && (
                <span className="px-1.5 py-0.2 rounded-full bg-rose-500/20 text-rose-300 border border-rose-500/40 text-[10px] font-mono font-semibold">
                  {securityIncidentCount}
                </span>
              )}
            </button>
          </div>

          {/* Right Controls: Polling cadence, Countdown, Manual Refresh, Config */}
          <div className="flex flex-wrap items-center justify-end gap-2.5 w-full lg:w-auto">
            
            {/* Polling Interval Selector */}
            <div className="flex items-center bg-slate-900/90 p-0.5 rounded-lg border border-slate-800 text-xs">
              <span className="text-slate-400 px-2 font-mono text-[11px] hidden sm:inline">Cadence:</span>
              {([3, 5, 10] as PollInterval[]).map((sec) => (
                <button
                  key={sec}
                  onClick={() => onChangeInterval(sec)}
                  className={`px-2 py-1 rounded font-mono text-[11px] font-medium transition-all ${
                    pollInterval === sec
                      ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30'
                      : 'text-slate-400 hover:text-slate-200'
                  }`}
                >
                  {sec}s
                </button>
              ))}
              <button
                onClick={() => onChangeInterval(0)}
                title="Pause automated polling"
                className={`px-2 py-1 rounded font-mono text-[11px] font-medium transition-all flex items-center gap-1 ${
                  isPaused
                    ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                    : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                <Pause className="w-3 h-3" />
                <span className="hidden sm:inline">Pause</span>
              </button>
            </div>

            {/* Countdown timer pill */}
            <div 
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-slate-900/90 border border-slate-800 text-xs font-mono"
              title={isPaused ? 'Polling is paused' : `Next poll in ${secondsRemaining}s`}
            >
              {isPaused ? (
                <span className="flex items-center gap-1 text-amber-400">
                  <Pause className="w-3 h-3" />
                  <span>PAUSED</span>
                </span>
              ) : (
                <>
                  <div className="relative w-4 h-4 flex items-center justify-center">
                    <svg className="w-4 h-4 -rotate-90">
                      <circle
                        cx="8"
                        cy="8"
                        r="6"
                        className="text-slate-800"
                        strokeWidth="2"
                        stroke="currentColor"
                        fill="transparent"
                      />
                      <circle
                        cx="8"
                        cy="8"
                        r="6"
                        className="text-cyan-400 transition-all duration-300 ease-linear"
                        strokeWidth="2"
                        strokeDasharray={37.7}
                        strokeDashoffset={37.7 * (1 - progressPct / 100)}
                        strokeLinecap="round"
                        stroke="currentColor"
                        fill="transparent"
                      />
                    </svg>
                  </div>
                  <span className="text-slate-300 w-6 text-right">{secondsRemaining}s</span>
                </>
              )}
            </div>

            {/* Refresh Now Button */}
            <button
              onClick={onRefreshNow}
              disabled={isPolling}
              title="Trigger instant telemetry refresh"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-800/80 hover:bg-slate-700/80 text-slate-200 hover:text-white border border-slate-700/70 text-xs font-medium transition-all active:scale-95 disabled:opacity-50 cursor-pointer"
            >
              <RotateCw className={`w-3.5 h-3.5 text-cyan-400 ${isPolling ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">{isPolling ? 'Syncing...' : 'Refresh'}</span>
            </button>

            {/* Gateway Settings / Auth Modal Trigger */}
            <button
              onClick={onOpenConfig}
              title="Configure Gateway URL & Bearer Token"
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-slate-800/80 hover:bg-slate-700/80 text-slate-300 hover:text-white border border-slate-700/70 text-xs font-medium transition-all active:scale-95 cursor-pointer"
            >
              <Sliders className="w-3.5 h-3.5 text-slate-300" />
              <span className="hidden md:inline">Config</span>
            </button>

          </div>

        </div>
      </div>
    </header>
  );
};
